import { useRef, useMemo, useEffect, useState } from 'react';
import AsyncSelect from 'react-select/async';
import { components } from 'react-select';
import { debounce } from 'lodash-es';
import { getCustomStyles } from '@/theme/components/select';

import { SearchMd } from '@kula-ai/icons-core';
import { AiScoreCandidateOption } from './AiScoreCandidateOption';
import { colors } from '@/theme/foundations/colors';
import { aiScoreTestApi } from './api/aiScoreTest.api';

const Control = ({ children, ...props }: any) => (
  <components.Control {...props}>
    <SearchMd
      size="1em"
      color="var(--kula-colors-secondary-800)"
      className="kula-icon"
      style={{ marginLeft: '6px' }}
    />
    {children}
  </components.Control>
);

export const AiScoreCandidateSelect = ({ onSelect }: any) => {
  const controllerRef = useRef<AbortController | null>(null);
  const [inputValue, setInputValue] = useState('');

  // Memoize the debounced search function
  const debouncedSearch = useMemo(
    () =>
      debounce(
        async (inputValue: string, callback: (options: any[]) => void) => {
          if (controllerRef.current) {
            controllerRef.current.abort();
          }
          const controller = new AbortController();
          controllerRef.current = controller;
          try {
            const data = await aiScoreTestApi.getContactDetails({
              searchTerm: inputValue,
              includeResume: false,
              signal: controller.signal,
            });
            const options = (data?.contact || []).map((contact: any) => ({
              ...contact,
              label: contact.first_name ?? ' ' + contact.last_name,
              value: contact.id,
            }));
            callback(options);
          } catch (err: any) {
            if (err?.name !== 'AbortError') {
              callback([]);
            }
          }
        },
        400
      ),
    []
  );

  // Clean up debounce on unmount
  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  return (
    <AsyncSelect
      onChange={(value) => onSelect(value)}
      inputValue={inputValue}
      onInputChange={(value, { action }) => {
        // Only set input when user is actually typing and prevent leading spaces
        if (action === 'input-change') {
          const cleanValue = value.startsWith(' ') ? value.trimStart() : value;
          setInputValue(cleanValue);
        }
      }}
      styles={getCustomStyles([
        { type: 'valueContainer', newStyles: { minHeight: '20px' } },
        {
          type: 'option',
          newStyles: {
            padding: '8px',
            borderRadius: '0px',
            borderBottom: `1px solid ${colors['border-color']}`,
            _last: {
              borderBottom: 'none',
            },
          },
        },
        {
          type: 'menu',
          newStyles: {
            padding: '0px',
          },
        },
      ])}
      placeholder="Search candidates"
      components={{
        IndicatorSeparator: null,
        DropdownIndicator: null,
        Control,
        Option: AiScoreCandidateOption,
      }}
      loadOptions={(searchValue, callback) => {
        const trimmedValue = searchValue?.trim();
        if (!trimmedValue || trimmedValue.length < 2) {
          callback([]);
          return;
        }
        debouncedSearch(trimmedValue, callback);
      }}
      noOptionsMessage={() => 'No candidates found'}
    />
  );
};
